import React from 'react';
import { getPOExcludableFields } from '../utils/api';

const LightningIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const POCreateModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  createData, 
  excludedFields, 
  onExcludedFieldsChange 
}) => {
  if (!isOpen || !createData) return null;

  const { summary, previewXml } = createData;
  const excludableFields = getPOExcludableFields();

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl flex flex-col max-h-[90vh] border border-gray-700">
        <div className="p-4 border-b border-gray-700 bg-green-500/10 rounded-t-lg flex items-center gap-2">
          <LightningIcon className="w-5 h-5 text-green-400" />
          <h3 className="font-bold text-white">Confirm PO Transaction Create</h3>
        </div>
        
        <div className="p-6 overflow-y-auto">
          <p className="text-gray-300 mb-4">
            You are about to create <span className="font-bold text-green-400">{summary.totalDocuments}</span> PO transaction(s) 
            with <span className="font-bold text-blue-400">{summary.totalLines}</span> line item(s).
          </p>
          
          {/* Documents Summary */}
          <div className="bg-gray-700 border border-gray-600 rounded p-3 mb-4">
            <p className="text-xs font-bold text-gray-400 uppercase mb-2">Documents to Create:</p>
            <div className="max-h-24 overflow-y-auto">
              {summary.documents.map((doc, idx) => (
                <div key={idx} className="flex justify-between items-center py-1 px-2 text-sm border-b border-gray-600 last:border-b-0">
                  <span className="font-mono text-white">{doc.docNo}</span>
                  <span className="text-xs text-gray-400">{doc.lineCount} {doc.lineCount === 1 ? 'line' : 'lines'}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Field Exclusion */}
          <div className="mb-4">
            <p className="text-xs font-bold text-gray-400 uppercase mb-2">Exclude Fields (click to toggle):</p>
            <div className="border border-gray-600 rounded p-3 bg-gray-700 max-h-40 overflow-y-auto">
              <div className="mb-2">
                <span className="text-xs font-semibold text-gray-400">Line Item Fields:</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {excludableFields.filter(f => f.group === 'line').map(field => (
                  <button
                    key={field.id}
                    onClick={() => !field.alwaysExcluded && onExcludedFieldsChange(field.id)}
                    disabled={field.alwaysExcluded}
                    className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                      field.alwaysExcluded
                        ? 'bg-gray-600 border-gray-500 text-gray-500 line-through cursor-not-allowed'
                        : excludedFields.includes(field.id)
                          ? 'bg-red-500/20 border-red-500/50 text-red-400 line-through'
                          : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                    }`}
                    title={field.alwaysExcluded ? 'Always excluded - for reference only' : 'Click to toggle'}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 mb-2">
                <span className="text-xs font-semibold text-gray-400">Header Fields:</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {excludableFields.filter(f => f.group === 'header').map(field => (
                  <button
                    key={field.id}
                    onClick={() => onExcludedFieldsChange(field.id)}
                    className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                      excludedFields.includes(field.id)
                        ? 'bg-red-500/20 border-red-500/50 text-red-400 line-through'
                        : 'bg-gray-600 border-gray-500 text-gray-300 hover:bg-gray-500'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Red/strikethrough = excluded from API call. Grey = always excluded (reference only).</p>
          </div>
          
          {/* XML Preview */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase mb-1">XML Preview (First Document)</p>
            <div className="bg-gray-900 p-3 rounded overflow-auto max-h-48 border border-gray-700">
              <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{previewXml}</pre>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t border-gray-700 bg-gray-900 flex justify-end gap-2 rounded-b-lg">
          <button 
            onClick={onClose} 
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded text-gray-300 font-medium hover:bg-gray-600"
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm} 
            className="px-4 py-2 bg-green-500 hover:bg-green-400 text-gray-900 rounded font-medium shadow-sm"
          >
            ⚡ Create Transactions
          </button>
        </div>
      </div>
    </div>
  );
};

export default POCreateModal;